import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, mergeProps as _mergeProps, createVNode as _createVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { class: "flex flex-col gap-8 md:gap-6" }

import { computed, ref, watch } from 'vue';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import { FormKit } from '@formkit/vue';
import { cloneDeep } from 'lodash-es';
import { AddressLookupService } from '@/services/store';
import type { IDropdownItem, IExperianSuggestion, IExperianResults, IExperianFormatResult } from '@/interfaces';
import { hideAllPoppersAndPaddings } from '@/plugins/tooltip/renderTooltip';
import RACQFormKit from '@/components/RACQFormKit/RACQFormKit.vue';
import RACQFormKitErrorMessage from '@/components/RACQFormKit/RACQFormKitErrorMessage.vue';
import { VueFormInputProps } from '@/composables/useFormInputData';
import {
  addressLookupEmits,
  emitAddressSelected,
  useFormStore,
  useJeopardyStore,
  useMultiInstanceAddressOptionsStore,
} from '@/store';
import { isPostalAddress } from '@/utils/regex-helpers';
import { RACQAddressResult } from '@/interfaces';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import { InformationBoxTypes } from '@/interfaces/forms/information-box.interface';
import { setFormkitInputValidationMessage } from '@/utils/formkit-helpers';
import { QLD } from '@/constants/general-words.const';


export default /*@__PURE__*/_defineComponent({
  __name: 'AddressLookup',
  props: {
    bindings: {},
    instanceIdentifier: {},
    innerClass: {},
    labelClass: {},
    defaultIsOverseasOption: { type: Boolean },
    showManualEntries: { type: Boolean },
    streetNoRequired: {},
    streetRequired: {},
    suburbRequired: {},
    stateRequired: {},
    postcodeRequired: {},
    countryRequired: {},
    checkAddressRequired: {},
    selectSuburbRequired: {},
    postcodeInvalid: {},
    invalidInsuranceRiskSuburbMessageTitle: {},
    invalidInsuranceRiskSuburbMessageText: {},
    postalSameAsInsuredAddressLabel: {},
    riskAddressNotFullyVerifiedMessage: {},
    riskAddressCycloneCheckFailedMessage: {},
    postalAddressTooltipVariant: {},
    riskAddressTooltipVariant: {},
    physicalAddressTooltipVariant: {},
    throwJpForNonQldAddress: {}
  },
  emits: addressLookupEmits,
  setup(__props: any, { emit: __emit }) {

const emit = __emit;

const props = __props;

const formStore = useFormStore();
const multiInstanceAddressStore = useMultiInstanceAddressOptionsStore();
const addressLookupOptionsStore = multiInstanceAddressStore.getInstance(props.instanceIdentifier);
const jpStore = useJeopardyStore();

const addressPicker = ref(null);
const displayAddressText = ref<string>();

const apiValidationMessage = computed(() => {
  if (addressLookupOptionsStore?.externalValidationMessage) {
    return { title: '', text: addressLookupOptionsStore?.externalValidationMessage };
  } else if (addressLookupOptionsStore.showInvalidInsuranceRiskSuburbMessage) {
    return { title: props.invalidInsuranceRiskSuburbMessageTitle, text: props.invalidInsuranceRiskSuburbMessageText };
  } else if (
    addressLookupOptionsStore.riskAddressNotFullyVerifiedIsError &&
    addressLookupOptionsStore.showRiskAddressNotFullyVerifiedMessage
  ) {
    return { title: '', text: props.riskAddressNotFullyVerifiedMessage };
  } else if (addressLookupOptionsStore.showRiskAddressCycloneCheckFailedMessage) {
    return { title: '', text: props.riskAddressCycloneCheckFailedMessage };
  }
  return null;
});

const modifiedBindings = computed(() => {
  let tempBindings = cloneDeep(props.bindings);

  if (!tempBindings.tooltip) return tempBindings;

  const usePostalAddressTooltip =
    addressLookupOptionsStore.config?.isPostalAddress && props.postalAddressTooltipVariant?.length > 0;
  const useRiskAddressTooltip =
    addressLookupOptionsStore.config?.allowPhysicalAddressesOnly &&
    addressLookupOptionsStore.config?.allowValidInsuranceRiskSuburbsOnly &&
    props.riskAddressTooltipVariant?.length > 0;
  const usePhysicalAddressTooltip =
    (!addressLookupOptionsStore.config?.isPostalAddress ||
      (addressLookupOptionsStore.config?.allowPhysicalAddressesOnly &&
        addressLookupOptionsStore.config?.allowValidInsuranceRiskSuburbsOnly)) &&
    props.riskAddressTooltipVariant?.length > 0;

  if (usePostalAddressTooltip) {
    tempBindings.tooltip.content = props.postalAddressTooltipVariant;
  } else if (useRiskAddressTooltip) {
    tempBindings.tooltip.content = props.riskAddressTooltipVariant;
  } else if (usePhysicalAddressTooltip) {
    tempBindings.tooltip.content = props.physicalAddressTooltipVariant;
  }
  return tempBindings;
});

// To watch changes from other components / steps
watch(
  () => addressLookupOptionsStore?.autoAddressComponents,
  (newValue) => {
    if (addressLookupOptionsStore.isManualEntries && newValue.addressLine != displayAddressText.value) {
      displayAddressText.value = newValue.addressLine;
    } else {
      const newAddress = addressLookupOptionsStore.buildAddressLine(newValue);

      // Do nothing if the component is in manual mode, keep the display text as No - I will enter manually
      if (newAddress != displayAddressText.value) {
        displayAddressText.value = newAddress;
      }
    }
  },
  { immediate: true }
);

watch(
  () => addressLookupOptionsStore?.externalValidationMessage,
  (newValue) => {
    if (newValue !== undefined && addressPicker.value && !addressLookupOptionsStore.isManualEntries) {
      setFormkitInputValidationMessage(addressPicker.value, newValue);
    }
  }
);

watch(displayAddressText, () => {
  if (!addressLookupOptionsStore.isManualEntries) {
    addressLookupOptionsStore.clearAllWarningsAndErrors();
  }
  setFormkitInputValidationMessage(addressPicker.value, '');
});

async function getAddressLookupFormatWithRetry(query: string): Promise<IExperianFormatResult | null> {
  try {
    return await AddressLookupService.getAddressLookupFormat(query);
  } catch (error) {
    console.error('Unable to format address, retrying once: ', error);
  }

  try {
    return await AddressLookupService.getAddressLookupFormat(query);
  } catch (error) {
    console.error('Unable to format address after retry: ', error);
    return null;
  }
}

function postalSameAsInsuredAddressChanged(event: any) {
  event.preventDefault();

  const useAddress = addressLookupOptionsStore.onSameAsPostalAddressChanged();

  //If the postal address has previously been saved as same as insured address and the same as insured address checkbox is unticked
  //we don't want the same address showing so we clear it.
  if (
    !addressLookupOptionsStore.postalSameAsInsuredAddressSelection &&
    addressLookupOptionsStore.checkPostalIsSameAsInsured(addressLookupOptionsStore.postalAddress as RACQAddressResult)
  ) {
    emit(emitAddressSelected, {});
    displayAddressText.value = '';
  } else {
    displayAddressText.value = addressLookupOptionsStore.buildAddressLine(useAddress!);
    emit(emitAddressSelected, useAddress);
  }
}

async function lookup({ search }: { search: string }): Promise<IDropdownItem[]> {
  try {
    if (!search || search.length < 7) return [];

    const data: IExperianResults = await AddressLookupService.getAddressLookup(search);

    if (addressLookupOptionsStore?.config?.allowPhysicalAddressesOnly) {
      data.results = data.results.filter((result) => {
        return !isPostalAddress(result.suggestion);
      });
    }

    data.results.push({
      matched: [],
      suggestion: props.bindings.postContent,
      format: '',
      triggerManualEntry: true,
    });

    return (
      data.results?.map((item: IExperianSuggestion) => ({
        label: item.suggestion,
        value: item,
      })) || []
    );
  } catch (error) {
    console.error('Unable to lookup: ', error);
    return [];
  }
}

async function applyRiskAddressBusinessRules() {
  if (addressLookupOptionsStore?.config?.allowValidInsuranceRiskSuburbsOnly) {
    formStore.showLoadingForCurrentStep();
    await addressLookupOptionsStore.applyRiskAddressBusinessRules();
    setFormkitInputValidationMessage(addressPicker.value, apiValidationMessage.value?.text);

    formStore.showLoadingForCurrentStep(false);
  }
}

function handleNonQldAddresses(state?: string): boolean {
  if (props.throwJpForNonQldAddress != null && state != null && state != QLD) {
    jpStore.showJeopardyPanel({
      jpCode: props.throwJpForNonQldAddress,
      isChatEnabled: true,
      isCallUsVisible: true,
    });

    return true;
  }

  return false;
}

async function handleSelection(address: IExperianSuggestion) {
  // Will be triggered when setting 'Not on list - I will enter manually' hence check for string.
  // If isManualEntries = true and no address exit to prevent resetManualAddress from hiding manual address.
  if (typeof address == 'string' || (!address && addressLookupOptionsStore.isManualEntries)) {
    return;
  }

  if (!address) {
    // hide manual entry fields if user cleared address lookup field
    addressLookupOptionsStore.resetManualAddress();
    return;
  } else if (address.triggerManualEntry) {
    if (addressLookupOptionsStore.isManualEntries) {
      return;
    }

    addressLookupOptionsStore.userHasTriggeredChanged = true;
    addressLookupOptionsStore.onEnableManualEntrySelected();

    setTimeout(() => {
      hideAllPoppersAndPaddings();
    }, 1000);
    return;
  }

  addressLookupOptionsStore.resetManualAddress();

  // Format Address
  if (address.format) {
    const data: IExperianFormatResult | null = await getAddressLookupFormatWithRetry(address.format.split('?')[1]);
    if (!data) {
      emit(emitAddressSelected, {});
      return;
    }

    /*
    Ticket: PJ342-3749
    Example: '1 PO Box 1, Logan City DC QLD 4114'
    Sitecore 8 will end up formatting it like this: 'PO Box 1, LOGAN CITY DC, QLD, 4114'
    
    If the address is a postal address.
    1. Clear street number.
    2. Set address line to be deliveryService1 which should contain 'PO Box 1'.
    */
    const isPostal = isPostalAddress(data.components?.find((comp) => comp.deliveryService1)?.deliveryService1 || '');
    const street =
      data.components?.find((comp) => comp.street1)?.street1 ||
      data.components?.find((comp) => comp.deliveryService1)?.deliveryService1;

    let streetNumber = data.components?.find((comp) => comp.streetNumber1)?.streetNumber1 || '';
    let addressLine = data.address
      .filter((comp) => comp.addressLine1 || comp.addressLine2 || comp.addressLine3)
      .map((item) => Object.values(item).toString())
      .join(' ');

    if (isPostal) {
      streetNumber = '';
      addressLine = data.components?.find((comp) => comp.deliveryService1)?.deliveryService1 || '';
    }

    addressLookupOptionsStore.autoAddressComponents = {
      postalDeliveryType: data.components?.find((comp) => comp.deliveryService1)?.deliveryService1 || '',
      addressLine: addressLine,
      unitNumber: data.components?.find((comp) => comp.subBuilding1)?.subBuilding1 || '',
      buildingLevel: data.components?.find((comp) => comp.subBuilding2)?.subBuilding2 || '',
      buildingName: data.components?.find((comp) => comp.building1)?.building1 || '',
      streetNumber: streetNumber,
      street: street,
      suburb: data.components?.find((comp) => comp.locality1)?.locality1 || '',
      postcode: data.components?.find((comp) => comp.postalCode1)?.postalCode1 || '',
      state: data.address?.find((comp) => comp.province)?.province || '',
      country: data.components?.find((comp) => comp.country1)?.country1 || '',
      countryISO: data.components?.find((comp) => comp.countryISO1)?.countryISO1 || '',
      dpid: data.components?.find((comp) => comp.deliveryPointId1)?.deliveryPointId1 || '',
    };

    // If a non-QLD address is selected and the throwJpForNonQldAddress prop has a value, throw a JP
    // and break out of this function early so no other validation rules/messages are executed
    if (handleNonQldAddresses(addressLookupOptionsStore.autoAddressComponents.state)) return;

    await applyRiskAddressBusinessRules();

    //Clear street before emitting the address.
    if (
      addressLookupOptionsStore.config?.allowPhysicalAddressesOnly &&
      isPostalAddress(addressLookupOptionsStore.autoAddressComponents.street ?? '')
    ) {
      addressLookupOptionsStore.autoAddressComponents.street = '';
    }

    addressLookupOptionsStore.onAutoAddressSelected(addressLookupOptionsStore.autoAddressComponents);

    emit(emitAddressSelected, addressLookupOptionsStore.autoAddressComponents);
  }
}

return (_ctx: any,_cache: any) => {
  return (_unref(addressLookupOptionsStore))
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        (_ctx.bindings.preContent)
          ? (_openBlock(), _createBlock(_unref(RichText), {
              key: 0,
              class: _normalizeClass(_ctx.bindings.preContentClass),
              field: { value: _ctx.bindings.preContent }
            }, null, 8, ["class", "field"]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_2, [
          (!_unref(addressLookupOptionsStore).isOverseasSelection)
            ? (_openBlock(), _createBlock(_unref(FormKit), _mergeProps({
                key: 0,
                ref_key: "addressPicker",
                ref: addressPicker
              }, modifiedBindings.value, {
                modelValue: displayAddressText.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((displayAddressText).value = $event)),
                options: lookup,
                type: "autocomplete",
                autocomplete: "off",
                maxLength: "255",
                "data-testid": `${_ctx.bindings.name}_auto_lookup`,
                name: _ctx.bindings.name + '_auto',
                innerClass: _ctx.innerClass || 'input-xl',
                outerClass: "",
                labelClass: _ctx.labelClass,
                listboxButtonClass: "$reset hidden",
                loaderIconClass: "$reset hidden",
                onInput: handleSelection
              }), null, 16, ["modelValue", "data-testid", "name", "innerClass", "labelClass"]))
            : _createCommentVNode("", true),
          _createVNode(RACQFormKitErrorMessage, {
            defaultMessagePlacement: !apiValidationMessage.value?.text,
            node: (addressPicker.value as any)?.node,
            errorTitle: apiValidationMessage.value?.title,
            errorMessage: apiValidationMessage.value?.text
          }, null, 8, ["defaultMessagePlacement", "node", "errorTitle", "errorMessage"]),
          (
          _unref(addressLookupOptionsStore) &&
          !_unref(addressLookupOptionsStore).isManualEntries &&
          !_unref(addressLookupOptionsStore).riskAddressNotFullyVerifiedIsError &&
          _unref(addressLookupOptionsStore).showRiskAddressNotFullyVerifiedMessage
        )
            ? (_openBlock(), _createBlock(InformationBox, {
                key: 1,
                description: _ctx.riskAddressNotFullyVerifiedMessage,
                type: 
          _unref(addressLookupOptionsStore).riskAddressNotFullyVerifiedIsError
            ? _unref(InformationBoxTypes).ERROR
            : _unref(InformationBoxTypes).WARNING
        
              }, null, 8, ["description", "type"]))
            : _createCommentVNode("", true),
          (
          _unref(addressLookupOptionsStore)?.config?.showPostalSameAsInsuredAddressSelection &&
          !_unref(addressLookupOptionsStore).isOverseasSelection
        )
            ? (_openBlock(), _createBlock(RACQFormKit, {
                key: 2,
                modelValue: _unref(addressLookupOptionsStore).postalSameAsInsuredAddressSelection,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(addressLookupOptionsStore).postalSameAsInsuredAddressSelection) = $event)),
                name: `${_ctx.bindings.name}_sameasinsured`,
                "data-testid": `${_ctx.bindings.name}_sameasinsured`,
                bindings: 
          {
            label: _ctx.postalSameAsInsuredAddressLabel,
            name: `${_ctx.bindings.name}__sameasinsured`,
            outerClass: `my-2`,
            type: 'checkbox',
            placeholder: '',
            classesOptions: '',
          } as VueFormInputProps
        ,
                onChange: postalSameAsInsuredAddressChanged
              }, null, 8, ["modelValue", "name", "data-testid", "bindings"]))
            : _createCommentVNode("", true)
        ])
      ]))
    : _createCommentVNode("", true)
}
}

})